import EN from "@/lang/strings_en";
import FR from "@/lang/strings_fr";
import NL from "@/lang/strings_nl";
import {SETTINGS_FIELDS, BLACKBOX_HIDDEN_SETTINGS, FIELD_READONLY} from "@/helpers/settingsSearchFields";
import {SETTINGS_TABS, allowedSettingsTabs} from "@/helpers/SettingsTabs";

const PANEL_TABS = {
    "SettingsOrgPanel": 0,
    "SettingsAppPanel": 1,
    "SettingsPrinterPanel": 2,
    "SettingsWebshopPanel": 3,
    "SettingsVivaPanel": 4,
    "SettingsBlackboxPanel": 5,
    "SettingsSuperUserPanel": 6,
};

const LOCALES = {fr: FR, en: EN, nl: NL};

export function normalizeSettingsText(value) {
    return String(value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "");
}

export function buildTranslationIndex(currentLocale) {
    const byKey = {};

    const walk = (node, path, lang) => {
        Object.entries(node || {}).forEach(([key, value]) => {
            const next = path ? `${path}.${key}` : key;
            if (value && typeof value === "object") {
                walk(value, next, lang);
                return;
            }
            if (typeof value !== "string") return;

            byKey[next] = byKey[next] || {values: []};
            byKey[next].values.push(value);
            if (lang === currentLocale) byKey[next].label = value;
        });
    };

    Object.entries(LOCALES).forEach(([lang, messages]) => walk(messages, "", lang));

    return byKey;
}

export function buildSettingsFieldRows(currentLocale) {
    const byKey = buildTranslationIndex(currentLocale);
    const isBlackbox = process.env.VUE_APP_CERTIFIED === "true";
    const rows = [];

    Object.entries(SETTINGS_FIELDS).forEach(([panel, fields]) => {
        const tabIndex = PANEL_TABS[panel];
        const tab = SETTINGS_TABS.find((item) => item.index === tabIndex);

        Object.entries(fields).forEach(([key, definition]) => {
            if (isBlackbox && BLACKBOX_HIDDEN_SETTINGS.has(key)) return;

            const entry = byKey[key];
            if (!entry?.label) return;

            const tooltip = byKey[`${key}Tooltip`];
            const parts = [key, definition.field, ...entry.values, ...(tooltip ? tooltip.values : [])];

            rows.push({
                id: `settingsField:${panel}:${key}`,
                key: key,
                panel: panel,
                tabIndex: tabIndex,
                tabTitle: tab ? tab.title : "",
                field: definition.field,
                type: definition.type,
                decimal: !!definition.decimal,
                superUserOnly: !!definition.superUserOnly,
                options: definition.options || [],
                label: entry.label,
                tooltip: tooltip ? tooltip.label : "",
                haystack: normalizeSettingsText(parts.join(" ")),
            });
        });
    });

    return rows;
}

export function searchSettingsFields(rows, search, user, isPhone, limit) {
    const words = normalizeSettingsText(search).split(/\s+/).filter(Boolean);
    if (!words.length) return [];

    const allowed = new Set(allowedSettingsTabs(user, isPhone).map((tab) => tab.index));
    const isSuperUser = user?.type === "SUPER_USER";
    const seen = new Set();
    const matches = [];

    for (const candidate of rows) {
        if (!allowed.has(candidate.tabIndex)) continue;
        const row = candidate.superUserOnly && !isSuperUser ? {...candidate, type: FIELD_READONLY} : candidate;
        if (!words.every((word) => row.haystack.includes(word))) continue;

        const signature = `${row.field}:${row.type}`;
        if (seen.has(signature)) continue;
        seen.add(signature);

        matches.push(row);
        if (matches.length >= limit) break;
    }

    return matches;
}
